import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Database,
  Search,
  BookOpen,
  Table,
  Filter,
  X,
  ChevronRight,
  ChevronDown,
  GitBranch,
  ArrowRight,
  Code,
  Sparkles,
  MessageCircle,
  Layers,
  Bot,
} from "lucide-react";
import { getDomainColor } from "../utils/formatting/domainColors";
import type { Dataset, TelegramUser, DatasetLayer } from "../types";
import { SchemaDocumentationModal } from "../components/SchemaDocumentationModal";
import { BusinessProcessesModal } from "../components/BusinessProcessesModal";
import { useMobile } from "../hooks/useMobile";
import { loadAllDomainSchemas } from "../utils/api/domains";
import type { LifecycleStep } from "../utils/api/domains";
import { OnboardingTour } from "../components/OnboardingTour";
import { AITutorDualModePanel } from "../components/AITutorDualModePanel";
import type { AITutorDualMode } from "../components/AITutorDualModePanel";

// ─── helpers ────────────────────────────────────────────────────────────────

function detectLayer(table: Dataset): DatasetLayer {
  if (table.layer) return table.layer;
  const n = table.name ?? "";
  if (n.startsWith("raw_")) return "raw";
  if (n.startsWith("stage_")) return "stage";
  if (n.startsWith("mart_")) return "mart";
  if (n.startsWith("dict_")) return "dict";
  return "raw";
}

function parseEngine(ddl?: string): string | null {
  if (!ddl) return null;
  if (/MATERIALIZED\s+VIEW/i.test(ddl)) return "MaterializedView";
  if (/CREATE\s+(OR\s+REPLACE\s+)?DICTIONARY/i.test(ddl)) return "Dictionary";
  const m = ddl.match(/ENGINE\s*=\s*([A-Za-z]+)/i);
  return m ? m[1] : null;
}

// ─── constants ───────────────────────────────────────────────────────────────

type LayerKey = DatasetLayer;

const LAYER_CONFIG: Record<
  LayerKey,
  { label: string; description: string; headerClass: string; badgeClass: string }
> = {
  raw: {
    label: "RAW",
    description: "Сырые данные из источников — JSON, Arrays, без трансформаций",
    headerClass: "bg-amber-50 border-amber-200 text-amber-900",
    badgeClass: "bg-amber-100 text-amber-800 border-amber-200",
  },
  stage: {
    label: "STAGE",
    description: "Очищенные и нормализованные данные со специализированными движками",
    headerClass: "bg-blue-50 border-blue-200 text-blue-900",
    badgeClass: "bg-blue-100 text-blue-800 border-blue-200",
  },
  mart: {
    label: "MART",
    description: "Аналитические витрины и агрегаты — готовы для запросов",
    headerClass: "bg-primary/5 border-primary/20 text-primary-dark",
    badgeClass: "bg-primary/10 text-primary-dark border-primary/20",
  },
  dict: {
    label: "DICT",
    description: "Словари ClickHouse — оптимизация JOIN, иерархии",
    headerClass: "bg-violet-50 border-violet-200 text-violet-900",
    badgeClass: "bg-violet-100 text-violet-800 border-violet-200",
  },
};

const ENGINE_COLORS: Record<string, string> = {
  MergeTree: "bg-gray-100 text-gray-700",
  ReplacingMergeTree: "bg-blue-100 text-blue-700",
  CollapsingMergeTree: "bg-orange-100 text-orange-700",
  SummingMergeTree: "bg-green-100 text-green-700",
  AggregatingMergeTree: "bg-purple-100 text-purple-700",
  Merge: "bg-yellow-100 text-yellow-700",
  Dictionary: "bg-violet-100 text-violet-700",
  MaterializedView: "bg-pink-100 text-pink-700",
};

const LAYER_ORDER: LayerKey[] = ["raw", "stage", "mart", "dict"];

// ─── types ───────────────────────────────────────────────────────────────────

interface DomainInfo {
  name: string;
  description: string;
  tables: Dataset[];
  color: string;
}

// ─── component ───────────────────────────────────────────────────────────────

export const DataDocs = ({ user }: { user?: TelegramUser | null }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // existing state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [selectedTableDatasets, setSelectedTableDatasets] = useState<Dataset[]>([]);
  const [isSchemaModalOpen, setIsSchemaModalOpen] = useState(false);
  const [initialTableName, setInitialTableName] = useState<string | undefined>(undefined);
  const [domainDescriptions, setDomainDescriptions] = useState<Record<string, string>>({});
  const [domainDetailInfo, setDomainDetailInfo] = useState<Record<string, string>>({});
  const [domainTables, setDomainTables] = useState<Record<string, Dataset[]>>({});
  const [domainRelations, setDomainRelations] = useState<Record<string, Array<[string, string]>>>({});
  const [isBusinessProcessesModalOpen, setIsBusinessProcessesModalOpen] = useState(false);
  const [selectedDomainForBusinessProcesses, setSelectedDomainForBusinessProcesses] = useState("");
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  const [ddlTable, setDdlTable] = useState<Dataset | null>(null);
  const [selectedTableRelations, setSelectedTableRelations] = useState<Array<[string, string]>>([]);

  // new state
  const [domainLifecycleSteps, setDomainLifecycleSteps] = useState<Record<string, LifecycleStep[]>>({});
  const [collapsedLayers, setCollapsedLayers] = useState<Set<string>>(new Set());
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiMode, setAiMode] = useState<AITutorDualMode>("chat");
  const [aiPromptId, setAiPromptId] = useState(0);
  const [aiPromptText, setAiPromptText] = useState("");

  const isMobile = useMobile();
  const onboardingStartRef = useRef<(() => void) | null>(null);

  // URL domain param
  useEffect(() => {
    const p = searchParams.get("domain");
    if (p) setSelectedDomain(p);
  }, [searchParams]);

  // Load domain data
  useEffect(() => {
    const load = async () => {
      try {
        const schemas = await loadAllDomainSchemas();
        const descriptions: Record<string, string> = {};
        const detailInfo: Record<string, string> = {};
        const tables: Record<string, Dataset[]> = {};
        const relations: Record<string, Array<[string, string]>> = {};
        const lifecycleSteps: Record<string, LifecycleStep[]> = {};

        schemas.forEach((schema) => {
          descriptions[schema.name] = schema.description ?? "";
          detailInfo[schema.name] = schema.detail_info ?? "";
          tables[schema.name] = schema.tables ?? [];
          relations[schema.name] = schema.relations ?? [];
          lifecycleSteps[schema.name] = schema.lifecycle_steps ?? [];
        });

        setDomainDescriptions(descriptions);
        setDomainDetailInfo(detailInfo);
        setDomainTables(tables);
        setDomainRelations(relations);
        setDomainLifecycleSteps(lifecycleSteps);
      } catch (e) {
        console.error("Ошибка загрузки доменов:", e);
      }
    };
    load();
  }, []);

  const domains = useMemo<DomainInfo[]>(() => {
    return Object.keys(domainTables).map((name) => ({
      name,
      description: domainDescriptions[name] ?? `Доменная область: ${name}`,
      tables: domainTables[name] ?? [],
      color: getDomainColor(name),
    }));
  }, [domainDescriptions, domainTables]);

  const filteredDomains = useMemo(() => {
    let result = domains;
    if (selectedDomain) result = result.filter((d) => d.name === selectedDomain);

    if (!searchQuery.trim()) return result.map((d) => ({ ...d }));

    const q = searchQuery.toLowerCase();
    return result
      .map((d) => {
        const domainMatch =
          d.name.toLowerCase().includes(q) || d.description.toLowerCase().includes(q);
        const filteredTables = d.tables.filter(
          (t) =>
            t.name?.toLowerCase().includes(q) ||
            t.description?.toLowerCase().includes(q) ||
            t.columns?.some(
              (c) =>
                c.name?.toLowerCase().includes(q) ||
                c.description?.toLowerCase().includes(q) ||
                c.type?.toLowerCase().includes(q)
            ) ||
            (t.ddl ?? "").toLowerCase().includes(q)
        );
        if (domainMatch) return { ...d };
        if (filteredTables.length > 0) return { ...d, tables: filteredTables };
        return null;
      })
      .filter((d): d is DomainInfo => d !== null);
  }, [domains, selectedDomain, searchQuery]);

  const generateDDL = (table: Dataset): string => {
    if (table.ddl) return table.ddl;
    const schema = table.schema ?? "default";
    if (!table.columns?.length) return `CREATE TABLE ${schema}.${table.name ?? "unknown"} ();`;
    const cols = table.columns
      .filter((c) => c?.name && c?.type)
      .map((c) => `  ${c.name} ${c.type}`)
      .join(",\n");
    return `CREATE TABLE ${schema}.${table.name ?? "unknown"} (\n${cols}\n) ENGINE = MergeTree()\nORDER BY tuple();`;
  };

  const toggleTableExpansion = (name: string) => {
    setExpandedTables((prev) => {
      const s = new Set(prev);
      s.has(name) ? s.delete(name) : s.add(name);
      return s;
    });
  };

  const toggleLayer = (key: string) => {
    setCollapsedLayers((prev) => {
      const s = new Set(prev);
      s.has(key) ? s.delete(key) : s.add(key);
      return s;
    });
  };

  const handleTableClick = (table: Dataset) => {
    const domain = domains.find((d) => d.tables.some((t) => t.name === table.name));
    setSelectedTableDatasets(domain ? domain.tables : [table]);
    setSelectedTableRelations(domain ? (domainRelations[domain.name] ?? []) : []);
    setInitialTableName(table.name ?? undefined);
    setIsSchemaModalOpen(true);
  };

  const askAIAboutTable = (table: Dataset, domainName: string) => {
    const text = `Расскажи о таблице \`${table.name}\` в домене ${domainName}: её назначение, ключевые колонки и когда её использовать в запросах`;
    setAiPromptText(text);
    setAiPromptId((n) => n + 1);
    setAiPanelOpen(true);
  };

  const askAIAboutDomain = (domainName: string) => {
    const text = `Расскажи об архитектуре данных в домене ${domainName}: какие слои данных есть (RAW/STAGE/MART/DICT), как они связаны и какие бизнес-процессы описывают`;
    setAiPromptText(text);
    setAiPromptId((n) => n + 1);
    setAiPanelOpen(true);
  };

  const onboardingSteps = useMemo(
    () => [
      {
        id: "domain-card",
        target: "[data-onboarding-id='domain-card']",
        title: "Доменные области",
        content:
          "Данные сгруппированы по доменным областям. Каждый домен содержит таблицы в 4 слоях: RAW (сырые), STAGE (очищенные), MART (витрины) и DICT (словари).",
        position: "bottom" as const,
      },
      {
        id: "lifecycle-stepper",
        target: "[data-onboarding-id='lifecycle-stepper']",
        title: "Жизненный цикл",
        content:
          "Здесь показан жизненный цикл данных в домене — от создания заказа до доставки клиенту.",
        position: "bottom" as const,
      },
      {
        id: "layer-section",
        target: "[data-onboarding-id='layer-section']",
        title: "Слои данных",
        content:
          "Таблицы разбиты по слоям: RAW → STAGE → MART → DICT. Кликните на заголовок слоя чтобы свернуть/развернуть.",
        position: "bottom" as const,
      },
      {
        id: "table-documentation",
        target: "[data-onboarding-id='table-documentation']",
        title: "Карточка таблицы",
        content:
          "Нажмите на карточку таблицы чтобы увидеть все колонки. Используйте кнопки для просмотра DDL, Lineage или вопроса AI Tutor.",
        position: "bottom" as const,
      },
      {
        id: "ask-ai",
        target: "[data-onboarding-id='ask-ai']",
        title: "AI Tutor",
        content:
          "Кнопка чата открывает AI Tutor с контекстом этой таблицы — задайте любой вопрос о данных.",
        position: "left" as const,
      },
    ],
    []
  );

  // ─── render helpers ────────────────────────────────────────────────────────

  const renderEngineBadge = (ddl?: string) => {
    const engine = parseEngine(ddl);
    if (!engine) return null;
    const cls = ENGINE_COLORS[engine] ?? "bg-gray-100 text-gray-700";
    return (
      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border border-current/20 ${cls}`}>
        {engine}
      </span>
    );
  };

  const renderLayerSection = (
    domain: DomainInfo,
    layer: LayerKey,
    tables: Dataset[]
  ) => {
    if (tables.length === 0) return null;
    const cfg = LAYER_CONFIG[layer];
    const collapseKey = `${domain.name}::${layer}`;
    const isCollapsed = collapsedLayers.has(collapseKey);

    return (
      <div key={layer} data-onboarding-id="layer-section" className="border border-neutral-border rounded-xl overflow-hidden">
        {/* Layer header */}
        <button
          className={`w-full flex items-center justify-between px-4 py-2.5 border-b ${cfg.headerClass} transition-colors hover:opacity-90`}
          onClick={() => toggleLayer(collapseKey)}
        >
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-2 py-0.5 rounded border ${cfg.badgeClass}`}>
              {cfg.label}
            </span>
            <span className="text-xs text-current/70 hidden sm:block">{cfg.description}</span>
            <span className="text-xs opacity-60">{tables.length} {tables.length === 1 ? "таблица" : tables.length < 5 ? "таблицы" : "таблиц"}</span>
          </div>
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4 opacity-60" />
          ) : (
            <ChevronDown className="w-4 h-4 opacity-60" />
          )}
        </button>

        {/* Tables grid */}
        {!isCollapsed && (
          <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"} gap-3 p-3 bg-white`}>
            {tables.map((table) => {
              const isExpanded = expandedTables.has(table.name);
              const engine = parseEngine(table.ddl);
              return (
                <div
                  key={table.name}
                  data-onboarding-id="table-documentation"
                  className="border border-neutral-border rounded-lg hover:border-primary hover:shadow-md transition-all bg-neutral-bg overflow-hidden cursor-pointer"
                  onClick={() => toggleTableExpansion(table.name)}
                >
                  <div className="p-3">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <Table className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                        <code className="font-mono text-xs font-semibold text-neutral-text break-all leading-tight">
                          {table.name}
                        </code>
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button
                          data-onboarding-id="ask-ai"
                          onClick={(e) => {
                            e.stopPropagation();
                            askAIAboutTable(table, domain.name);
                          }}
                          className="p-1 rounded hover:bg-primary/10 text-neutral-text/50 hover:text-primary transition-colors"
                          title="Спросить AI"
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDdlTable(table);
                          }}
                          className="p-1 rounded hover:bg-primary/10 text-neutral-text/50 hover:text-primary transition-colors"
                          title="Показать DDL"
                        >
                          <Code className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTableClick(table);
                          }}
                          className="p-1 rounded hover:bg-primary/10 text-neutral-text/50 hover:text-primary transition-colors"
                          title="Lineage"
                        >
                          <GitBranch className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {engine && (
                      <div className="mb-1.5">
                        {renderEngineBadge(table.ddl)}
                      </div>
                    )}

                    {table.description && (
                      <p className={`text-[11px] text-neutral-text/70 ${isExpanded ? "" : "line-clamp-2"} mb-1.5`}>
                        {table.description}
                      </p>
                    )}

                    {!isExpanded && table.columns && (
                      <p className="text-[10px] text-neutral-text/40">
                        {table.columns.length} колонок · нажмите для просмотра
                      </p>
                    )}
                  </div>

                  {isExpanded && table.columns && table.columns.length > 0 && (
                    <div className="px-3 pb-3 pt-1 border-t border-neutral-border">
                      <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                        {table.columns.map((col) => (
                          <div key={col.name} className="text-[11px]">
                            <div className="flex items-baseline gap-1.5 flex-wrap">
                              <code className="font-mono font-semibold text-neutral-text">{col.name}</code>
                              <span className="text-neutral-text/40 font-mono">{col.type}</span>
                            </div>
                            {col.description && (
                              <p className="text-neutral-text/55 mt-0.5 leading-tight">{col.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderLifecycleStepper = (steps: LifecycleStep[]) => {
    if (!steps.length) return null;
    return (
      <div data-onboarding-id="lifecycle-stepper" className="flex items-center gap-1 flex-wrap">
        {steps.map((step, idx) => (
          <div key={step.step} className="flex items-center gap-1">
            <div className="flex items-center gap-1.5 bg-white/30 backdrop-blur-sm rounded-lg px-2 py-1">
              <span className="w-5 h-5 flex items-center justify-center rounded-full bg-white/60 text-xs font-bold text-neutral-text">
                {step.step}
              </span>
              <span className="text-xs font-medium">{step.title}</span>
            </div>
            {idx < steps.length - 1 && (
              <ChevronRight className="w-3 h-3 opacity-50 flex-shrink-0" />
            )}
          </div>
        ))}
      </div>
    );
  };

  // ─── main render ──────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-neutral-light pt-20 pb-12">
      <OnboardingTour
        steps={onboardingSteps}
        storageKey="onboarding-datadocs-v2-completed"
        user={user}
        onStartRef={onboardingStartRef}
      />

      {/* Hero section */}
      <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-b border-neutral-border mb-6">
        <div className={`max-w-7xl mx-auto ${isMobile ? "px-4 py-6" : "px-6 py-8"}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-primary/15 rounded-xl flex items-center justify-center">
                  <Database className="w-5 h-5 text-primary" />
                </div>
                <h1 className={`${isMobile ? "text-2xl" : "text-3xl"} font-bold text-neutral-text`}>
                  Data Documentation
                </h1>
              </div>
              <p className={`${isMobile ? "text-sm" : "text-base"} text-neutral-text/65 max-w-2xl`}>
                Документация по схеме данных logistics: таблицы, слои RAW → STAGE → MART → DICT,
                связи между таблицами и бизнес-процессы.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => {
                  setAiPanelOpen((v) => !v);
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-all ${
                  aiPanelOpen
                    ? "bg-primary text-white border-primary shadow-md"
                    : "bg-white text-neutral-text border-neutral-border hover:border-primary hover:text-primary"
                }`}
                title="AI Tutor"
              >
                <Bot className="w-4 h-4" />
                {!isMobile && "AI Tutor"}
              </button>
              <button
                onClick={() => onboardingStartRef.current?.()}
                className="p-2 bg-white border border-neutral-border rounded-xl hover:border-primary hover:text-primary text-neutral-text/60 transition-colors"
                title="Как пользоваться"
              >
                <Sparkles className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Stats row */}
          <div className="flex items-center gap-4 mt-4 flex-wrap">
            {[
              { label: "Доменов", value: domains.length },
              { label: "Таблиц", value: domains.reduce((s, d) => s + d.tables.length, 0) },
              { label: "Слоёв", value: 4 },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center gap-1.5 text-sm">
                <span className="font-bold text-primary">{value}</span>
                <span className="text-neutral-text/60">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main layout */}
      <div className={`max-w-7xl mx-auto ${isMobile ? "px-3" : "px-6"}`}>
        <div className={`${!isMobile && aiPanelOpen ? "flex gap-6 items-start" : ""}`}>

          {/* Left: Docs content */}
          <div className="flex-1 min-w-0">
            {/* Search + filters */}
            <div className="mb-5 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text/40" />
                <input
                  type="text"
                  placeholder="Поиск по доменам, таблицам, колонкам..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 border border-neutral-border rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-text/40 hover:text-neutral-text"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="flex flex-wrap gap-2 items-center">
                <Filter className="w-3.5 h-3.5 text-neutral-text/50" />
                <button
                  onClick={() => setSelectedDomain(null)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium border transition-colors ${
                    selectedDomain === null
                      ? "bg-primary text-white border-primary"
                      : "bg-white text-neutral-text border-neutral-border hover:border-primary hover:text-primary"
                  }`}
                >
                  Все домены
                </button>
                {domains.map((domain) => (
                  <button
                    key={domain.name}
                    onClick={() => setSelectedDomain(domain.name === selectedDomain ? null : domain.name)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium border transition-colors ${
                      selectedDomain === domain.name
                        ? `${domain.color} border-current`
                        : "bg-white text-neutral-text border-neutral-border hover:border-primary hover:text-primary"
                    }`}
                  >
                    {domain.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Domain cards */}
            <div className="space-y-5">
              {filteredDomains.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-neutral-border">
                  <Search className="w-8 h-8 text-neutral-text/30 mx-auto mb-3" />
                  <p className="text-neutral-text/60">Ничего не найдено по запросу «{searchQuery}»</p>
                </div>
              ) : (
                filteredDomains.map((domain) => {
                  const lifecycleSteps = domainLifecycleSteps[domain.name] ?? [];

                  // group tables by layer
                  const tablesByLayer: Partial<Record<LayerKey, Dataset[]>> = {};
                  for (const table of domain.tables) {
                    const layer = detectLayer(table);
                    if (!tablesByLayer[layer]) tablesByLayer[layer] = [];
                    tablesByLayer[layer]!.push(table);
                  }

                  return (
                    <div
                      key={domain.name}
                      data-onboarding-id="domain-card"
                      className="bg-white rounded-xl border border-neutral-border shadow-sm overflow-hidden"
                    >
                      {/* Domain header */}
                      <div className={`${domain.color} ${isMobile ? "p-4" : "p-5"} border-b border-black/5`}>
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Layers className="w-4 h-4 opacity-70 flex-shrink-0" />
                              <h2 className={`${isMobile ? "text-lg" : "text-xl"} font-bold leading-tight`}>
                                {domain.name}
                              </h2>
                            </div>
                            <p className="text-sm opacity-80">{domain.description}</p>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {domainDetailInfo[domain.name] && (
                              <button
                                title="Бизнес-процессы"
                                onClick={() => {
                                  setSelectedDomainForBusinessProcesses(domain.name);
                                  setIsBusinessProcessesModalOpen(true);
                                }}
                                className="p-1.5 rounded-lg bg-white/25 hover:bg-white/40 transition-colors"
                              >
                                <BookOpen className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              title="Lineage домена"
                              onClick={() => {
                                setSelectedTableDatasets(domain.tables);
                                setSelectedTableRelations(domainRelations[domain.name] ?? []);
                                setInitialTableName(undefined);
                                setIsSchemaModalOpen(true);
                              }}
                              className="p-1.5 rounded-lg bg-white/25 hover:bg-white/40 transition-colors"
                            >
                              <GitBranch className="w-4 h-4" />
                            </button>
                            <button
                              title="Спросить AI об этом домене"
                              onClick={() => askAIAboutDomain(domain.name)}
                              className="p-1.5 rounded-lg bg-white/25 hover:bg-white/40 transition-colors"
                            >
                              <MessageCircle className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Lifecycle stepper */}
                        {lifecycleSteps.length > 0 && renderLifecycleStepper(lifecycleSteps)}

                        {/* Layer counts */}
                        <div className="flex items-center gap-3 mt-3 flex-wrap">
                          {(Object.entries(tablesByLayer) as [LayerKey, Dataset[]][]).map(([layer, tables]) => (
                            <span key={layer} className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${LAYER_CONFIG[layer].badgeClass}`}>
                              {LAYER_CONFIG[layer].label}: {tables.length}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Layer sections */}
                      <div className={`${isMobile ? "p-3" : "p-4"} space-y-3`}>
                        {LAYER_ORDER.map((layer) => {
                          const tables = tablesByLayer[layer];
                          if (!tables) return null;
                          return renderLayerSection(domain, layer, tables);
                        })}

                        {/* Navigate to tasks */}
                        <button
                          onClick={() => navigate(`/simulator?domain=${encodeURIComponent(domain.name)}`)}
                          className="w-full flex items-center justify-between p-3 border border-neutral-border rounded-xl hover:border-primary hover:bg-primary/5 transition-all group"
                        >
                          <div className="flex items-center gap-2.5">
                            <BookOpen className="w-4 h-4 text-primary" />
                            <div className="text-left">
                              <p className="text-sm font-semibold text-neutral-text">Задачи по домену</p>
                              <p className="text-xs text-neutral-text/55">Практика с реальными запросами</p>
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-neutral-text/40 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right: AI Sidebar (desktop only) */}
          {!isMobile && aiPanelOpen && (
            <div className="w-[380px] flex-shrink-0 sticky top-24 h-[calc(100vh-7rem)] rounded-xl overflow-hidden shadow-lg border border-neutral-border">
              <AITutorDualModePanel
                user={user ?? null}
                onClose={() => setAiPanelOpen(false)}
                embedded={true}
                mode={aiMode}
                onModeChange={setAiMode}
                showCloseButton={true}
                chatProps={{
                  contextType: "generic",
                  embedded: true,
                  headerSubtitle: "Спросить о данных",
                  enableFeedback: true,
                  externalPrompt:
                    aiPromptText
                      ? { id: String(aiPromptId), text: aiPromptText }
                      : undefined,
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Mobile: floating AI button */}
      {isMobile && (
        <button
          onClick={() => navigate("/ai-tutor")}
          className="fixed bottom-6 right-4 z-40 flex items-center gap-2 px-4 py-3 bg-primary text-white rounded-full shadow-xl hover:bg-primary-dark transition-all"
        >
          <Bot className="w-5 h-5" />
          <span className="text-sm font-medium">AI Tutor</span>
        </button>
      )}

      {/* Schema / Lineage modal */}
      <SchemaDocumentationModal
        datasets={selectedTableDatasets}
        isOpen={isSchemaModalOpen}
        onClose={() => setIsSchemaModalOpen(false)}
        relations={selectedTableRelations}
        initialTableName={initialTableName}
      />

      {/* Business processes modal */}
      {selectedDomainForBusinessProcesses && domainDetailInfo[selectedDomainForBusinessProcesses] && (
        <BusinessProcessesModal
          domainName={selectedDomainForBusinessProcesses}
          detailInfo={domainDetailInfo[selectedDomainForBusinessProcesses]}
          isOpen={isBusinessProcessesModalOpen}
          onClose={() => {
            setIsBusinessProcessesModalOpen(false);
            setSelectedDomainForBusinessProcesses("");
          }}
        />
      )}

      {/* DDL modal */}
      {ddlTable && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setDdlTable(null)}
        >
          <div
            className={`bg-white rounded-xl shadow-2xl ${isMobile ? "max-w-full w-full" : "max-w-4xl w-full"} max-h-[90vh] overflow-hidden flex flex-col`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-neutral-border flex items-center justify-between">
              <h3 className="text-base font-semibold text-neutral-text">
                DDL · <code className="font-mono text-primary">{ddlTable.name}</code>
              </h3>
              <button
                onClick={() => setDdlTable(null)}
                className="p-1.5 rounded hover:bg-neutral-light text-neutral-text/60 hover:text-primary transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-auto flex-1">
              <pre className="bg-neutral-light p-4 rounded-xl overflow-x-auto">
                <code className="text-sm font-mono text-neutral-text whitespace-pre">
                  {generateDDL(ddlTable)}
                </code>
              </pre>
            </div>
            <div className="p-4 border-t border-neutral-border flex justify-end gap-2">
              <button
                onClick={() => navigator.clipboard.writeText(generateDDL(ddlTable))}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm"
              >
                Копировать
              </button>
              <button
                onClick={() => setDdlTable(null)}
                className="px-4 py-2 border border-neutral-border rounded-lg hover:bg-neutral-light transition-colors text-sm"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
